import { AutoSuggest as KiamaAutoSuggest } from '@snsw/react-component-library';
import { forwardRef, ReactNode, RefObject, useImperativeHandle, useRef, useState } from 'react';
import { useBoolean, useDebounce, useLatest, useMountedState } from 'react-use';
import styled from 'styled-components';
import { PropsWithForwardedRef } from 'types/types';
import { AutoSuggestBusy } from './AutoSuggestBusy';

/** The reason codes passed to `shouldRenderSuggestions`. See: https://github.com/moroshko/react-autosuggest#shouldrendersuggestions-optional */
export type ShouldRenderSuggestionReason =
  | 'input-changed' // user typed something
  | 'input-focused' // input was focused
  | 'input-blurred' // input was un-focused
  | 'escape-pressed' // user pressed Escape to clear the input (and suggestions are shown for empty input)
  | 'suggestions-revealed' // user pressed Up or Down to reveal suggestions
  | 'suggestions-updated' // the suggestions were updated
  | 'render'; // the component is re-rendering

export interface AutoSuggestProps<TSuggestion> {
  /** The `id` attribute of the input control */
  id?: string;

  /** The `name` attribute of the input control */
  name?: string;

  /** Label to display above the field */
  label: ReactNode;

  /** Help message to display under the field */
  helpMessage?: ReactNode;

  /** Disables input */
  disabled?: boolean;

  /** ARIA required value */
  isRequired?: boolean;

  /** ARIA optional value */
  isOptional?: boolean;

  /** Is the component currently in an error state? */
  hasError?: boolean;

  /** Error message to show for the component */
  errorMessage?: string;

  /** Initial value to show in the component */
  initialValue?: TSuggestion | string;

  /** Optional callback invoked when the input value changes */
  onValueChange?: (value: string) => void;

  /** Optional callback invoked when the user leaves the input field */
  onBlur?: () => void;

  /** The current list of suggestions based on the last value */
  suggestions?: TSuggestion[];

  /** Callback invoked when the value has changed and a new list of suggestions needs to be fetched */
  onGetSuggestions?: (value: string) => void | Promise<void>;

  /** Callback invoked to clear the suggestion list */
  onClearSuggestions?: () => void;

  /** Callback to get the value to display in the input field when a suggestion is clicked */
  getSuggestionValue?: (suggestion: TSuggestion) => string;

  /** Optional callback invoked when the user selects a suggestion value */
  onSelect?: (suggestion: TSuggestion) => void;

  /** Optional callback to change how the suggestion gets rendered. Defaults to `value => value`. */
  renderSuggestion?: (suggestion: TSuggestion) => ReactNode;

  /**
   * Stops the "Busy" suggestion from showing during network searches
   */
  hideBusy?: boolean;

  /** Callback invoked to determine if the suggestions should be rendered based on the current input text. */
  shouldRenderSuggestions?: true | ((value: string, reason: ShouldRenderSuggestionReason) => boolean);

  /** Placeholder text to show in the input field when it is empty */
  placeholder?: string;

  /** Minimum length required in the input field before suggestions will be changed */
  minLength?: number;

  /** Optional class name to use for the field */
  className?: string;

  /** Optional number of milliseconds to wait between fetching suggestions. If zero, no debounce will be added. */
  debounceMillis?: number;
}

const KiamaAutoSuggestStyled = styled(KiamaAutoSuggest)`
  div[role='listbox'] {
    z-index: 5;
  }
`;

const AutoSuggestWithRef = <TSuggestion extends any>(
  {
    debounceMillis,
    suggestions,
    onGetSuggestions,
    shouldRenderSuggestions,
    getSuggestionValue,
    minLength,
    initialValue,
    hideBusy,
    ...props
  }: AutoSuggestProps<TSuggestion>,
  ref: RefObject<HTMLInputElement>
) => {
  const autoSuggestRef = useRef<HTMLInputElement>();
  useImperativeHandle(ref, () => autoSuggestRef.current);

  const { isSearching, handleGetSuggestions } = useGetSuggestionsHandler(onGetSuggestions, debounceMillis);

  // Kiama's `minLength` property also has a bug, where it doesn't cancel out (via `shouldRenderSuggestions`) if the
  // user exceeds the min length, and then deletes their input. For example, if the `minLength` is 3, and the user
  // types 'dog' then the suggestions would show. If the user then backspaces so the input now says 'do', then the
  // suggestions should be hidden.
  //
  // We work around this by using `minLength` to change how `shouldRenderSuggestions` works, unless the user has
  // already passed a `shouldRenderSuggestions` function, in which case we just respect their decision.
  const handleShouldRenderSuggestions =
    shouldRenderSuggestions ?? minLength >= 0 ? createShouldRenderSuggestionHandler(minLength) : undefined;

  // If no value was provided for `getSuggestionValue`, but the suggestions are objects, the underlying Autosuggest
  // throws an obscure error - "cannot update state during render". To cater for this, we will supply a default
  // getSuggestionValue method that warns the user that they should supply their own version.
  const handleGetSuggestionValue = useGetSuggestionValueHandler(getSuggestionValue);

  const initialValueText =
    typeof initialValue === 'string'
      ? initialValue
      : initialValue != null
      ? handleGetSuggestionValue(initialValue)
      : '';

  return (
    <>
      <KiamaAutoSuggestStyled
        {...props}
        inputRef={autoSuggestRef}
        initialValue={initialValueText}
        onGetSuggestions={handleGetSuggestions}
        shouldRenderSuggestions={handleShouldRenderSuggestions}
        getSuggestionValue={handleGetSuggestionValue}
        suggestions={suggestions}
        /*
         Since we are overriding the way `minLength` works, we need to give the Kiama control a non-zero value.
         */
        minLength={-1}
        /*
          The Kiama AutoSuggest was clearly built for their address search; since we are using it as a generic
          AutoSuggest, we never want it to display the "Can't find your address?" suggestion.
         */
        showManualAddress={false}
      />

      {isSearching && !hideBusy && <AutoSuggestBusy inputRef={autoSuggestRef} />}
    </>
  );
};

export const AutoSuggest = forwardRef(AutoSuggestWithRef) as <TSuggestion extends any = any>(
  props: PropsWithForwardedRef<AutoSuggestProps<TSuggestion>, HTMLInputElement>
) => ReturnType<typeof AutoSuggestWithRef>;

//////////

function createShouldRenderSuggestionHandler<TSuggestion extends any>(minLength: number) {
  return (value: string | TSuggestion) => {
    return typeof value === 'string' && value.trim().length >= minLength;
  };
}

function useGetSuggestionValueHandler<TSuggestion extends any>(
  getSuggestionValue: AutoSuggestProps<TSuggestion>['getSuggestionValue']
) {
  const hasShownWarningRef = useRef(false);

  if (getSuggestionValue) return getSuggestionValue;

  return (value: TSuggestion) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();

    if (!hasShownWarningRef.current) {
      console.warn(
        'No value was provided for `getSuggestionValue`, but the suggestions are objects. Please provide an ' +
          'implementation for `getSuggestionValue` to return the string value to be displayed in the text box for ' +
          'a suggestion.'
      );

      hasShownWarningRef.current = true;
    }

    return value.toString();
  };
}

function useGetSuggestionsHandler<TSuggestion extends any>(
  onGetSuggestions: AutoSuggestProps<TSuggestion>['onGetSuggestions'],
  debounceMillis: AutoSuggestProps<TSuggestion>['debounceMillis']
) {
  const [searchText, setSearchText] = useState('');
  const [isSearching, toggleSearching] = useBoolean(false);

  const isMounted = useMountedState();
  const onGetSuggestionsRef = useLatest(onGetSuggestions);

  useDebounce(
    async () => {
      try {
        if (onGetSuggestionsRef.current != null) {
          await onGetSuggestionsRef.current(searchText);
        }
      } finally {
        isMounted() && toggleSearching(false);
      }
    },
    debounceMillis,
    [searchText]
  );

  const handleGetSuggestions = (value: string) => {
    if (onGetSuggestionsRef.current == null) return;

    setSearchText(value);
    toggleSearching(value !== searchText);
  };

  return {
    isSearching,
    handleGetSuggestions,
  };
}
